import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { OrderingOption } from 'generated/graphql-types';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddPromptComponent } from '../../add-prompt/containers/add-prompt.component';
import { convertPromptRegisterListItem } from '../helpers/prompts-helper';
import { PromptRegisterOptions } from '../models/prompt-register-options';
import { PromptRegisterResults } from '../models/prompt-register-results.model';
import { PromptsFilterOption } from '../models/prompts-filter-option';
import { GetPrompts } from '../queries/generated/GetPrompts';
import { PromptRegisterApiService } from '../services/prompt-register-api.service';
import { PromptRegisterSessionStorageService } from '../services/prompt-register-session.storage.service';
import {
   createPrompt,
   promptRegisterAdapterActions,
   setOrderingOptionsFromSessionStorage,
} from './prompt-register.actions';
import {
   PromptRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './prompt-register.reducer';
import * as PromptRegisterSelectors from './prompt-register.selectors';
import { selectFilters } from './prompt-register.selectors';

@Injectable()
export class PromptRegisterEffects {
   addPrompt$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createPrompt),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddPromptComponent, {
                  data: {
                     excludedPromptTypes: ['SystemPrompt'],
                     includeReusableOption: false,
                     includeAddAnother: true,
                  },
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadPromptList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(promptRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, filters, quickFilters, orderingOptions]) => {
            const sessionOrderingOptions = this.promptRegisterSessionStorageService.getPromptOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadPromptPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(promptRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(PromptRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, endCursor, quickFilters, orderingOptions]) => {
            const sessionOrderingOptions = this.promptRegisterSessionStorageService.getPromptOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(promptRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(PromptRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            this.promptRegisterSessionStorageService.setPromptOrderingOptions(orderingOptions);
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(promptRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            this.promptRegisterSessionStorageService.setPromptOrderingOptions(orderingOptions);
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<PromptRegisterState>,
      private promptRegisterApiService: PromptRegisterApiService,
      private dialogService: LcsDialogService,
      private router: Router,
      private promptRegisterSessionStorageService: PromptRegisterSessionStorageService
   ) {}

   private convertGetPromptsResultToLoadSuccessProps(getPrompts: GetPrompts): PromptRegisterResults {
      return {
         endCursor: getPrompts.promptsConnection.pageInfo.endCursor,
         hasNextPage: getPrompts.promptsConnection.pageInfo.hasNextPage,
         items: getPrompts.promptsConnection.items.map(convertPromptRegisterListItem.bind(this)),
         totalCount: getPrompts.promptsConnection.totalCount,
      };
   }

   private loadNewData(
      filters: PromptsFilterOption[],
      quickFilters: PromptRegisterOptions | null = null,
      orderingOptions: OrderingOption[] | null = null
   ) {
      return this.promptRegisterApiService.getPrompts(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return promptRegisterAdapterActions.loadSuccess(
               this.convertGetPromptsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(promptRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: PromptsFilterOption[],
      quickFilters: PromptRegisterOptions | null = null,
      orderingOptions: OrderingOption[] | null = null
   ) {
      return this.promptRegisterApiService.getPrompts(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return promptRegisterAdapterActions.loadPageSuccess(
               this.convertGetPromptsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(promptRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }
}
